// Area: views (owner views-grid, wave 2). Saved views of an object (tabs, drafts saved with "Save
// view"), sidebar favourites, teams a view can be shared with and the CSV export of a view or a
// selection. Board signals became conditional badges (views-layouts): `query` evaluates `rules`.
import {
  CreateViewInput,
  DuplicateViewInput,
  ExportRequest,
  FavoriteViewInput,
  ReorderViewsInput,
  TeamDTO,
  UpdateViewInput,
  ViewDTO,
} from '@dataroom/core/views';
import { z } from 'zod';
import { defineContract } from '../define';

export {
  CreateViewInput,
  DuplicateViewInput,
  ExportRequest,
  FavoriteViewInput,
  ReorderViewsInput,
  TeamDTO,
  UpdateViewInput,
  ViewDTO,
};

const Key = z.object({ key: z.string().min(1).max(63) });
const KeyView = Key.extend({ view: z.string().min(1).max(80) });

export const listViewsContract = defineContract({
  method: 'GET',
  path: '/objects/:key/views',
  params: Key,
  response: z.array(ViewDTO),
  permission: 'records.read',
  summary: 'Saved views of an object the caller can see, in tab order',
});

export const createViewContract = defineContract({
  method: 'POST',
  path: '/objects/:key/views',
  params: Key,
  body: CreateViewInput,
  response: ViewDTO,
  status: 201,
  permission: 'records.read',
  summary: 'Create a view (layout, filter, sort, grouping, columns, rules, visibility)',
});

export const reorderViewsContract = defineContract({
  method: 'POST',
  path: '/objects/:key/views/reorder',
  params: Key,
  body: ReorderViewsInput,
  response: z.array(ViewDTO),
  permission: 'records.read',
  summary: 'Reorder the view tabs (for everyone)',
});

export const getViewContract = defineContract({
  method: 'GET',
  path: '/objects/:key/views/:view',
  params: KeyView,
  response: ViewDTO,
  permission: 'records.read',
  summary: 'One view by id or key',
});

export const updateViewContract = defineContract({
  method: 'PATCH',
  path: '/objects/:key/views/:view',
  params: KeyView,
  body: UpdateViewInput,
  response: ViewDTO,
  permission: 'records.read',
  summary: 'Save a view ("Save view"); settings only by its owner, locked views only by their owner',
});

export const deleteViewContract = defineContract({
  method: 'DELETE',
  path: '/objects/:key/views/:view',
  params: KeyView,
  response: z.void(),
  permission: 'records.read',
  summary: 'Delete a view (not the last one)',
});

export const duplicateViewContract = defineContract({
  method: 'POST',
  path: '/objects/:key/views/:view/duplicate',
  params: KeyView,
  body: DuplicateViewInput,
  response: ViewDTO,
  status: 201,
  permission: 'records.read',
  summary: 'Duplicate a view as a private copy',
});

export const favoriteViewContract = defineContract({
  method: 'PUT',
  path: '/objects/:key/views/:view/favorite',
  params: KeyView,
  body: FavoriteViewInput,
  response: ViewDTO,
  permission: 'records.read',
  summary: 'Pin or unpin a view in the sidebar favourites of the caller',
});

export const viewTeamsContract = defineContract({
  method: 'GET',
  path: '/views/teams',
  response: z.array(TeamDTO),
  summary: 'Teams a view can be shared with',
});

/**
 * CSV export of a view (filter, sort, search `q`, columns) or of selected `ids`. The response is the
 * CSV file (`text/csv`, `content-disposition` with the file name, `x-export-rows`).
 */
export const exportViewContract = defineContract({
  method: 'POST',
  path: '/objects/:key/export',
  params: Key,
  body: ExportRequest,
  response: z.string(),
  permission: 'records.export',
  summary: 'CSV export of a view (filter, sort, search, columns) or of selected records',
});
